const SNAPSHOT_PUBLISH_INTERVAL_MS = 250;
const WINDOW_DURATION_MS = 1000;

export interface EngineTelemetryFrameSample {
  readonly timestampMs: number;
  readonly frameTimeMs: number;
  readonly updateCount: number;
  readonly updateDurationMs: number;
  readonly isRendered: boolean;
  readonly renderDurationMs: number;
  readonly isFrameTimeCapped: boolean;
}

export interface EngineTelemetrySnapshot {
  readonly timestampMs: number;
  readonly uptimeSec: number;
  readonly fps: number;
  readonly updatesPerSecond: number;
  readonly rendersPerSecond: number;
  readonly averageFrameTimeMs: number;
  readonly averageUpdateTimeMs: number;
  readonly averageRenderTimeMs: number;
  readonly averageBusyTimeMs: number;
  readonly busyPercent: number;
  readonly lastFrameTimeMs: number;
  readonly lastUpdateTimeMs: number;
  readonly lastRenderTimeMs: number;
  readonly maxFrameTimeMs: number;
  readonly maxUpdateTimeMs: number;
  readonly maxRenderTimeMs: number;
  readonly catchUpFrameCount: number;
  readonly cappedFrameCount: number;
  readonly totalFrameCount: number;
  readonly totalUpdateCount: number;
  readonly totalRenderCount: number;
}

type TelemetryListener = (snapshot: EngineTelemetrySnapshot) => void;

interface WindowTotals {
  frameCount: number;
  updateCount: number;
  renderCount: number;
  frameTimeMs: number;
  updateTimeMs: number;
  renderTimeMs: number;
  busyTimeMs: number;
  catchUpFrameCount: number;
  cappedFrameCount: number;
  maxFrameTimeMs: number;
  maxUpdateTimeMs: number;
  maxRenderTimeMs: number;
}

const EMPTY_WINDOW_TOTALS: WindowTotals = {
  frameCount: 0,
  updateCount: 0,
  renderCount: 0,
  frameTimeMs: 0,
  updateTimeMs: 0,
  renderTimeMs: 0,
  busyTimeMs: 0,
  catchUpFrameCount: 0,
  cappedFrameCount: 0,
  maxFrameTimeMs: 0,
  maxUpdateTimeMs: 0,
  maxRenderTimeMs: 0,
};

function divideOrZero(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

function toRate(count: number, elapsedMs: number): number {
  return divideOrZero(count * 1000, elapsedMs);
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function roundMetric(value: number): number {
  return Math.round(value * 10) / 10;
}

export function createEmptyEngineTelemetrySnapshot(): EngineTelemetrySnapshot {
  return {
    timestampMs: 0,
    uptimeSec: 0,
    fps: 0,
    updatesPerSecond: 0,
    rendersPerSecond: 0,
    averageFrameTimeMs: 0,
    averageUpdateTimeMs: 0,
    averageRenderTimeMs: 0,
    averageBusyTimeMs: 0,
    busyPercent: 0,
    lastFrameTimeMs: 0,
    lastUpdateTimeMs: 0,
    lastRenderTimeMs: 0,
    maxFrameTimeMs: 0,
    maxUpdateTimeMs: 0,
    maxRenderTimeMs: 0,
    catchUpFrameCount: 0,
    cappedFrameCount: 0,
    totalFrameCount: 0,
    totalUpdateCount: 0,
    totalRenderCount: 0,
  };
}

export class EngineTelemetry {
  private snapshot: EngineTelemetrySnapshot = createEmptyEngineTelemetrySnapshot();
  private readonly listeners = new Set<TelemetryListener>();
  private startedAtMs: number | null = null;
  private windowStartedAtMs: number | null = null;
  private windowTotals: WindowTotals = { ...EMPTY_WINDOW_TOTALS };
  private totalFrameCount = 0;
  private totalUpdateCount = 0;
  private totalRenderCount = 0;
  private lastPublishedAtMs = 0;

  getSnapshot(): EngineTelemetrySnapshot {
    return this.snapshot;
  }

  subscribe(listener: TelemetryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  recordFrame(sample: EngineTelemetryFrameSample): void {
    if (this.startedAtMs === null) {
      this.startedAtMs = sample.timestampMs;
    }

    if (this.windowStartedAtMs === null) {
      // The first frame's time ran before its timestamp: the window starts there, so
      // the first snapshot reports that frame's rate, not one frame in a millisecond.
      this.windowStartedAtMs = sample.timestampMs - sample.frameTimeMs;
    }

    const busyDurationMs = sample.updateDurationMs + sample.renderDurationMs;
    const catchUpFrame = sample.updateCount > 1 ? 1 : 0;
    const renderedCount = sample.isRendered ? 1 : 0;

    this.totalFrameCount += 1;
    this.totalUpdateCount += sample.updateCount;
    this.totalRenderCount += renderedCount;

    this.windowTotals.frameCount += 1;
    this.windowTotals.updateCount += sample.updateCount;
    this.windowTotals.renderCount += renderedCount;
    this.windowTotals.frameTimeMs += sample.frameTimeMs;
    this.windowTotals.updateTimeMs += sample.updateDurationMs;
    this.windowTotals.renderTimeMs += sample.renderDurationMs;
    this.windowTotals.busyTimeMs += busyDurationMs;
    this.windowTotals.catchUpFrameCount += catchUpFrame;
    this.windowTotals.cappedFrameCount += sample.isFrameTimeCapped ? 1 : 0;
    this.windowTotals.maxFrameTimeMs = Math.max(this.windowTotals.maxFrameTimeMs, sample.frameTimeMs);
    this.windowTotals.maxUpdateTimeMs = Math.max(this.windowTotals.maxUpdateTimeMs, sample.updateDurationMs);
    this.windowTotals.maxRenderTimeMs = Math.max(this.windowTotals.maxRenderTimeMs, sample.renderDurationMs);

    const uptimeMs = sample.timestampMs - this.startedAtMs;
    const windowElapsedMs = Math.max(sample.timestampMs - this.windowStartedAtMs, 1);

    this.snapshot = {
      timestampMs: sample.timestampMs,
      uptimeSec: roundMetric(uptimeMs / 1000),
      fps: roundMetric(toRate(this.windowTotals.frameCount, windowElapsedMs)),
      updatesPerSecond: roundMetric(toRate(this.windowTotals.updateCount, windowElapsedMs)),
      rendersPerSecond: roundMetric(toRate(this.windowTotals.renderCount, windowElapsedMs)),
      averageFrameTimeMs: roundMetric(divideOrZero(this.windowTotals.frameTimeMs, this.windowTotals.frameCount)),
      averageUpdateTimeMs: roundMetric(divideOrZero(this.windowTotals.updateTimeMs, this.windowTotals.updateCount)),
      averageRenderTimeMs: roundMetric(divideOrZero(this.windowTotals.renderTimeMs, this.windowTotals.renderCount)),
      averageBusyTimeMs: roundMetric(divideOrZero(this.windowTotals.busyTimeMs, this.windowTotals.frameCount)),
      busyPercent: roundMetric(clampPercent(divideOrZero(this.windowTotals.busyTimeMs * 100, windowElapsedMs))),
      lastFrameTimeMs: roundMetric(sample.frameTimeMs),
      lastUpdateTimeMs: roundMetric(sample.updateDurationMs),
      lastRenderTimeMs: roundMetric(sample.renderDurationMs),
      maxFrameTimeMs: roundMetric(this.windowTotals.maxFrameTimeMs),
      maxUpdateTimeMs: roundMetric(this.windowTotals.maxUpdateTimeMs),
      maxRenderTimeMs: roundMetric(this.windowTotals.maxRenderTimeMs),
      catchUpFrameCount: this.windowTotals.catchUpFrameCount,
      cappedFrameCount: this.windowTotals.cappedFrameCount,
      totalFrameCount: this.totalFrameCount,
      totalUpdateCount: this.totalUpdateCount,
      totalRenderCount: this.totalRenderCount,
    };

    if (
      this.lastPublishedAtMs === 0
      || (sample.timestampMs - this.lastPublishedAtMs) >= SNAPSHOT_PUBLISH_INTERVAL_MS
    ) {
      this.lastPublishedAtMs = sample.timestampMs;
      for (const listener of this.listeners) {
        listener(this.snapshot);
      }
    }

    if (windowElapsedMs >= WINDOW_DURATION_MS) {
      this.windowStartedAtMs = sample.timestampMs;
      this.windowTotals = { ...EMPTY_WINDOW_TOTALS };
    }
  }
}

export function formatEngineTelemetryConsole(snapshot: EngineTelemetrySnapshot): string {
  return [
    '[engine]',
    `fps=${snapshot.fps.toFixed(1)}`,
    `ups=${snapshot.updatesPerSecond.toFixed(1)}`,
    `rps=${snapshot.rendersPerSecond.toFixed(1)}`,
    `frame=${snapshot.averageFrameTimeMs.toFixed(1)}ms`,
    `update=${snapshot.averageUpdateTimeMs.toFixed(2)}ms`,
    `render=${snapshot.averageRenderTimeMs.toFixed(2)}ms`,
    `busy=${snapshot.busyPercent.toFixed(1)}%`,
    `catch-up=${snapshot.catchUpFrameCount}`,
    `capped=${snapshot.cappedFrameCount}`,
  ].join(' ');
}
